import { immerable } from 'immer';
import { isString } from 'api/helpers';

import { CameraCCTVModel, ICameraCCTVModel } from './camera-cctv.model';
import { CameraImageModel, ICameraImageModel } from './camera-image.model';
import { IProjectModel, ProjectModel } from './project.model';

/* -------------------------------------------------------------------------------------------------
 * ICameraModel
 * -----------------------------------------------------------------------------------------------*/
export interface ICameraModel {
    _id: string;
    name: string;
    location: {
        name: string;
        latitude?: string;
        longitude?: string;
    };
    installationDate: string;
    status: string;
    orientation?: string;
    enabledFeaturesList: string[];
    hasImages: boolean;
    angle: number;
    distance: number;
    fieldOfView: number;
    latestImage?: Omit<ICameraImageModel, '_id' | 'camera'>;
    cameraCctv: string | ICameraCCTVModel;
    project: string | IProjectModel;
    urlThumb?: string;
    startDate?: string;
    endDate?: string;
    lastUpdated?: string;
}

/* -------------------------------------------------------------------------------------------------
 * CameraModel
 * -----------------------------------------------------------------------------------------------*/
export class CameraModel {
    [immerable] = true;

    _id: string;
    name: string;
    installationDate: string;
    status: string;
    orientation: string;
    enabledFeaturesList: string[];
    hasImages: boolean;
    angle: number;
    distance: number;
    fieldOfView: number;
    location: {
        name: string;
        latitude?: string;
        longitude?: string;
    };

    // Relationships
    latestImage?: Omit<CameraImageModel, '_id' | 'camera'>;
    cameraCctv: string | CameraCCTVModel;
    project: string | ProjectModel;
    urlThumb?: string;
    startDate?: string;
    endDate?: string;
    lastUpdated?: string;

    constructor(data: Partial<ICameraModel>) {
        this._id = data._id ?? '';
        this.name = data.name ?? '';
        this.installationDate = data.installationDate ?? '';
        this.status = data.status ?? '';
        this.orientation = data.orientation ?? '0';
        this.enabledFeaturesList = data.enabledFeaturesList ?? [];
        this.hasImages = data.hasImages ?? null;
        this.urlThumb = data.urlThumb ?? '';
        this.angle = data.angle ?? null;
        this.distance = data.distance ?? null;
        this.fieldOfView = data.fieldOfView ?? null;
        this.startDate = data.startDate ?? '';
        this.endDate = data.endDate ?? '';
        this.lastUpdated = data.lastUpdated ?? '';
        this.location = {
            name: data.location?.name ?? '',
            latitude: data.location?.latitude ?? '',
            longitude: data.location?.longitude ?? '',
        };

        if (data.latestImage) {
            this.latestImage = CameraImageModel.fromObject(data.latestImage);
        } else {
            this.latestImage = null;
        }

        if (data.cameraCctv) {
            if (isString(data.cameraCctv)) {
                this.cameraCctv = data.cameraCctv as string;
            } else {
                this.cameraCctv = CameraCCTVModel.fromObject(data.cameraCctv as ICameraCCTVModel);
            }
        }

        if (data.project) {
            if (isString(data.project)) {
                this.project = data.project as string;
            } else {
                this.project = ProjectModel.fromObject(data.project as IProjectModel);
            }
        }
    }

    static fromObject(data: Partial<ICameraModel>): CameraModel {
        const model = new CameraModel(data);

        return model;
    }

    static fromObjectList(data: Partial<ICameraModel>[]): CameraModel[] {
        return data.map((row) => CameraModel.fromObject(row));
    }

    static cloneFrom(data: Partial<ICameraModel>): CameraModel {
        const model = new CameraModel(data);

        return model;
    }

    toObject(): ICameraModel {
        const data: Partial<ICameraModel> = {
            _id: this._id,
            name: this.name,
            installationDate: this.installationDate || null,
            status: this.status,
            orientation: this.orientation,
            enabledFeaturesList: this.enabledFeaturesList,
            hasImages: this.hasImages,
            latestImage: this.latestImage,
            angle: this.angle,
            distance: this.distance,
            fieldOfView: this.fieldOfView,
            urlThumb: this.urlThumb,
            startDate: this.startDate,
            endDate: this.endDate,
            lastUpdated: this.lastUpdated,
            location: {
                name: this.location.name || null,
                latitude: this.location.latitude || null,
                longitude: this.location.longitude || null,
            },
        };

        if (this.cameraCctv) {
            if (this.cameraCctv instanceof CameraCCTVModel) {
                data.cameraCctv = this.cameraCctv.toObject();
            } else {
                data.cameraCctv = this.cameraCctv;
            }
        }

        if (this.project) {
            if (this.project instanceof ProjectModel) {
                data.project = this.project.toObject();
            } else {
                data.project = this.project;
            }
        }

        return data as ICameraModel;
    }
}
